import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { copyText } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Boxes, Copy, ExternalLink } from "lucide-react";

/**
 * Sprint 9 — Konfigurator-Verwaltung (/configurator).
 * Mandant/Agency-Admin bearbeitet Texte, Status, Notification & holt Embed-Code.
 */
export default function ConfiguratorAdmin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isBlocked = user?.role === "client_user";
  const [cfg, setCfg] = useState(null);
  const [subs, setSubs] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isBlocked) {
      nav("/dashboard", { replace: true });
    }
  }, [isBlocked, nav]);

  useEffect(() => {
    if (isBlocked) return;
    let alive = true;
    (async () => {
      const [a, b] = await Promise.all([
        api.get("/configurator"),
        api.get("/configurator/submissions?limit=10").catch(() => ({ data: [] })),
      ]);
      if (!alive) return;
      setCfg(a.data);
      setSubs(b.data || []);
    })();
    return () => { alive = false; };
  }, [isBlocked]);

  if (isBlocked) return null;
  if (!cfg) return <div className="p-8 text-slate-500">Wird geladen …</div>;

  const set = (k, v) => setCfg((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/configurator", {
        status: cfg.status,
        title: cfg.title,
        subtitle: cfg.subtitle,
        cta_text: cfg.cta_text,
        thank_you_text: cfg.thank_you_text,
        redirect_url: cfg.redirect_url || "",
        notification_email: cfg.notification_email || "",
        notification_enabled: !!cfg.notification_enabled,
        send_lead_confirmation: cfg.send_lead_confirmation !== false,
      });
      setCfg(r.data);
      toast.success("Konfigurator gespeichert");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const embedUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/embed/configurator/${cfg.configurator_id}`;
  const iframeCode = `<iframe src="${embedUrl}" width="100%" height="760" style="border:0" loading="lazy" title="PV-Konfigurator"></iframe>`;

  const copyEmbed = async () => {
    const ok = await copyText(iframeCode);
    if (ok) toast.success("Embed-Code kopiert");
    else toast.error("Kopieren fehlgeschlagen — bitte Code manuell markieren");
  };

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="configurator-admin-root">
      <div className="flex items-center gap-2">
        <Boxes className="w-5 h-5 text-orange-600" />
        <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
          PV-Konfigurator
        </h1>
      </div>
      <p className="text-sm text-slate-500 -mt-3">
        Einbettbarer PV-Konfigurator für Ihre Website. Sammelt strukturierte Leads, ohne Login, im CI Ihrer Solarfirma.
      </p>

      {/* Card 1: Status */}
      <Card className="p-6 border-slate-200" data-testid="cfg-status-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</p>
            <p className="text-sm text-slate-900 mt-1">
              {cfg.status === "active" ? "Der Konfigurator ist aktiv und nimmt Anfragen entgegen." : "Der Konfigurator ist deaktiviert."}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">Aktiv</span>
            <Switch checked={cfg.status === "active"}
                    onCheckedChange={(v) => set("status", v ? "active" : "inactive")}
                    data-testid="cfg-status-toggle" />
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <Stat label="Einsendungen gesamt" value={cfg.submission_count || 0} testid="cfg-kpi-count" />
          <Stat label="Letzte Einsendung" value={cfg.last_submission_at ? new Date(cfg.last_submission_at).toLocaleString("de-DE") : "—"} />
          <Stat label="Konfigurator-ID" value={cfg.configurator_id} testid="cfg-id-value" />
        </div>
      </Card>

      {/* Card 2: Texte */}
      <Card className="p-6 border-slate-200 space-y-3" data-testid="cfg-texts-card">
        <h2 className="text-lg font-semibold">Texte &amp; Optionen</h2>
        <Row label="Titel"><Input value={cfg.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="cfg-title" /></Row>
        <Row label="Untertitel"><Input value={cfg.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} data-testid="cfg-subtitle" /></Row>
        <Row label="Button-Text"><Input value={cfg.cta_text || ""} onChange={(e) => set("cta_text", e.target.value)} data-testid="cfg-cta" /></Row>
        <Row label="Danke-Text"><Textarea value={cfg.thank_you_text || ""} onChange={(e) => set("thank_you_text", e.target.value)} rows={3} data-testid="cfg-thanks" /></Row>
        <Row label="Weiterleitungs-URL (optional)"><Input value={cfg.redirect_url || ""} onChange={(e) => set("redirect_url", e.target.value)} placeholder="https://…" data-testid="cfg-redirect" /></Row>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
          <Row label="Notification-E-Mail (optional, sonst Support-E-Mail des Mandanten)">
            <Input value={cfg.notification_email || ""} onChange={(e) => set("notification_email", e.target.value)} placeholder="leads@solar-mueller.de" data-testid="cfg-notif-email" />
          </Row>
          <div className="flex items-end gap-2">
            <Switch checked={!!cfg.notification_enabled} onCheckedChange={(v) => set("notification_enabled", v)} data-testid="cfg-notif-toggle" />
            <span className="text-sm text-slate-600">Bei neuer Einsendung E-Mail an Mandant senden</span>
          </div>
        </div>
        <div className="pt-3 border-t border-slate-100">
          <div className="flex items-start gap-3" data-testid="cfg-lead-confirm-row">
            <Switch
              checked={cfg.send_lead_confirmation !== false}
              onCheckedChange={(v) => set("send_lead_confirmation", v)}
              data-testid="cfg-lead-confirm-toggle"
              className="mt-0.5"
            />
            <div>
              <p className="text-sm text-slate-700 font-medium">Eingangsbestätigung an Lead senden</p>
              <p className="text-xs text-slate-500 mt-0.5">Nach dem Absenden erhält der Lead eine kurze Bestätigung an seine E-Mail. Kein Angebot, kein Link, keine Preisaussagen.</p>
            </div>
          </div>
        </div>
        <div className="pt-3">
          <Button onClick={save} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="cfg-save-btn">
            {saving ? "Speichern …" : "Speichern"}
          </Button>
        </div>
      </Card>

      {/* Card 3: Embed Code */}
      <Card className="p-6 border-slate-200 space-y-3" data-testid="cfg-embed-card">
        <h2 className="text-lg font-semibold">Embed-Code</h2>
        <p className="text-sm text-slate-500">Kopieren Sie diesen Code und fügen Sie ihn an der gewünschten Stelle Ihrer Website ein.</p>
        <pre className="text-xs bg-slate-900 text-slate-50 p-3 rounded-md overflow-auto" data-testid="cfg-embed-code">{iframeCode}</pre>
        <div className="flex gap-2">
          <Button variant="outline" onClick={copyEmbed} data-testid="cfg-embed-copy">
            <Copy className="w-4 h-4 mr-1.5" /> Code kopieren
          </Button>
          <a href={embedUrl} target="_blank" rel="noreferrer">
            <Button variant="outline" data-testid="cfg-embed-open">
              <ExternalLink className="w-4 h-4 mr-1.5" /> Vorschau öffnen
            </Button>
          </a>
        </div>
      </Card>

      {/* Card 4: Live-Preview */}
      <Card className="p-6 border-slate-200" data-testid="cfg-preview-card">
        <h2 className="text-lg font-semibold mb-3">Live-Vorschau</h2>
        <iframe src={embedUrl} title="Vorschau" className="w-full" style={{ height: 720, border: 0 }} data-testid="cfg-preview-iframe" />
      </Card>

      {/* Card 5: Letzte Einsendungen */}
      <Card className="p-6 border-slate-200" data-testid="cfg-submissions-card">
        <h2 className="text-lg font-semibold mb-3">Letzte Einsendungen</h2>
        {subs.length === 0 ? (
          <p className="text-sm text-slate-500">Noch keine Einsendungen.</p>
        ) : (
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md">
            {subs.map((s) => (
              <li key={s.lead_id} className="px-3 py-2 text-sm flex items-center gap-3" data-testid={`cfg-sub-${s.lead_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">
                    {(s.first_name + " " + s.last_name).trim() || "(ohne Namen)"}
                  </div>
                  <div className="text-xs text-slate-500">{s.email || s.phone || "—"} · {new Date(s.created_at).toLocaleString("de-DE")}</div>
                </div>
                <a href={`/leads/${s.lead_id}`} className="text-xs text-orange-600 hover:underline">öffnen</a>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

const Row = ({ label, children }) => (
  <div>
    <Label className="text-xs text-slate-500 uppercase tracking-wider">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);
const Stat = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
    <p className="text-sm text-slate-900 mt-0.5">{value}</p>
  </div>
);
